import React, { useMemo } from 'react';
import { Package, Plus, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { PlansFiltersBar } from '../../../../components/filters/PlansFiltersBar';

export const WorkOrdersTable = ({
    // Maintenance plan props
    // Execution or Asignation Mode (removes create button)
    mode,
    // Removes plan selection if false
    isSuperior = true,
    plans,
    // MaintPlan Filter Props
    searchQuery,
    setSearchQuery,
    filterAsset,
    setFilterAsset,
    filterPriority,
    setFilterPriority,
    filterMaintenanceType,
    setFilterMaintenanceType,
    uniqueAssets,
    uniquePriorities,
    uniqueMaintenanceTypes,
    // If no argument is passed, create empty function to avoid error
    onCreate = (() => { }),

    filteredPlansCount,

    // Work orders props
    workOrders,
    // Pagination Props
    totalItems,
    totalPages,
    rangeStart,
    rangeEnd,
    page,
    setPage,
    pageSize,
    setPageSize,
    pageSizeOptions,
    // WO Filter Props
    searchQueryWO,
    setSearchQueryWO,
    filterAssetWO,
    setFilterAssetWO,
    filterPriorityWO,
    setFilterPriorityWO,
    filterMaintenanceTypeWO,
    setFilterMaintenanceTypeWO,
    filterStatusWO,
    setFilterStatusWO,
    uniqueAssetsWO,
    uniquePrioritiesWO,
    uniqueMaintenanceTypesWO,
    uniqueStatusesWO,
    //Selection Props
    selectedPlanId,
    setSelectedPlanId,
    isLoading,
    onWorkOrderClick,
    selectedWorkOrderId,
}) => {



    return (
        <div className="rounded-2xl rounded-tl-none border border-gray-700 bg-gray-800 shadow-lg w-full min-w-0 min-h-[420px] flex flex-col">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-700 shrink-0">
                {/* Header*/}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <Package className="w-5 h-5 text-gray-400 shrink-0" />
                        <h2 className="text-xl font-semibold text-white">
                            Work Orders List
                        </h2>
                        <Badge className="bg-blue-900/50 text-blue-200">
                            {totalItems} {totalItems === 1 ? 'work order' : 'work orders'}
                        </Badge>
                    </div>
                </div>

                {/* Maintenance Plan Selection (only if role is superior)*/}
                {isSuperior &&
                    <div className="mb-3 border-b border-gray-700 pb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Maintenance Plan Filter
                        </label>
                        <div className="mb-3">
                            <PlansFiltersBar
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                filterAsset={filterAsset}
                                setFilterAsset={setFilterAsset}
                                filterPriority={filterPriority}
                                setFilterPriority={setFilterPriority}
                                filterMaintenanceType={filterMaintenanceType}
                                setFilterMaintenanceType={setFilterMaintenanceType}
                                uniqueAssets={uniqueAssets}
                                uniquePriorities={uniquePriorities}
                                uniqueMaintenanceTypes={uniqueMaintenanceTypes}
                            />
                            <p className="mt-2 text-xs text-gray-400">
                                {filteredPlansCount} {filteredPlansCount === 1 ? 'plan available' : 'plans available'}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <select
                                value={selectedPlanId || ''}
                                onChange={(e) => setSelectedPlanId(e.target.value || null)}
                                className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">All Plans</option>
                                {plans.map(plan => (
                                    <option key={plan.id || plan.ID} value={plan.id || plan.ID}>
                                        {plan.name || plan.Name}
                                    </option>
                                ))}
                            </select>
                            {/* Remove create Work Order button if table is in the execution screen */}
                            {mode !== 'exec' &&
                                <button
                                    type="button"
                                    onClick={onCreate}
                                    className={`inline-flex w-1/5 items-center justify-center gap-2 px-4 py-2 rounded-lg border border-green-600 bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                                    disabled={selectedPlanId === null}
                                >
                                    <Plus className="w-4 h-4" />
                                    Create
                                </button>
                            }
                        </div>
                    </div>}

                {/* Work Orders Filters */}
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Work Order Filters
                    </label>
                    <PlansFiltersBar
                        searchQuery={searchQueryWO}
                        setSearchQuery={setSearchQueryWO}
                        filterAsset={filterAssetWO}
                        setFilterAsset={setFilterAssetWO}
                        filterPriority={filterPriorityWO}
                        setFilterPriority={setFilterPriorityWO}
                        filterMaintenanceType={filterMaintenanceTypeWO}
                        setFilterMaintenanceType={setFilterMaintenanceTypeWO}
                        filterStatus={filterStatusWO}
                        setFilterStatus={setFilterStatusWO}
                        uniqueAssets={uniqueAssetsWO}
                        uniquePriorities={uniquePrioritiesWO}
                        uniqueMaintenanceTypes={uniqueMaintenanceTypesWO}
                        uniqueStatuses={uniqueStatusesWO}
                    />
                </div>
            </div>

            {/* Content Area */}
            <div className="p-4 sm:p-6 w-full min-w-0 flex-1 flex flex-col">
                {isLoading ? (
                    <div className="text-center py-16">
                        <p className="text-gray-400">Loading work orders...</p>
                    </div>
                ) : workOrders.length === 0 ? (
                    <div className="text-center py-16">
                        <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400 text-lg">
                            No work orders found.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="w-full flex-1 min-h-0 overflow-y-auto">
                            {/* Table View */}
                            <div className="w-full">
                                <div className="inline-block w-full align-middle">
                                    <div className="shadow ring-1 ring-gray-700 rounded-lg w-full">
                                        <table className="w-full divide-y divide-gray-800">
                                            <thead className="sticky top-0 z-0 bg-gray-700/50 shadow-sm">
                                                <tr>
                                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                                        Title
                                                    </th>
                                                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                                        Asset
                                                    </th>
                                                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                                        Type
                                                    </th>
                                                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                                        Priority
                                                    </th>
                                                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-gray-800 divide-y divide-gray-700">
                                                {workOrders.map((workOrder, index) => (

                                                    <tr
                                                        key={workOrder.id}
                                                        onClick={() => onWorkOrderClick(workOrder)}
                                                        className={`transition-colors cursor-pointer ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-770'
                                                            } ${new Date(workOrder.scheduled_date) < new Date() && workOrder.status === 'scheduled' && 'bg-red-600/30'}
                                                         hover:bg-gray-700 ${selectedWorkOrderId === workOrder.id ? 'ring-2 ring-blue-500 ring-inset' : ''
                                                            }`}
                                                    >
                                                        <td className="px-6 py-4 whitespace-nowrap text-left">
                                                            <span className="text-sm font-medium text-white">
                                                                {workOrder.title || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            <Badge className="bg-violet-900/50 text-violet-200">
                                                                {workOrder.asset_name || 'N/A'}
                                                            </Badge>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-center">
                                                            {workOrder.work_order_type ? workOrder.work_order_type.charAt(0).toUpperCase() + workOrder.work_order_type.slice(1) : 'N/A'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-center">
                                                            <Badge className={
                                                                workOrder.priority?.toLowerCase() === 'critical' ? 'bg-red-900/50 text-red-200' :
                                                                    workOrder.priority?.toLowerCase() === 'high' ? 'bg-orange-900/50 text-orange-200' :
                                                                        workOrder.priority?.toLowerCase() === 'medium' ? 'bg-yellow-900/50 text-yellow-200' :
                                                                            'bg-green-900/50 text-green-200'
                                                            }>
                                                                {workOrder.priority ? workOrder.priority.charAt(0).toUpperCase() + workOrder.priority.slice(1) : 'N/A'}
                                                            </Badge>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                                            <Badge className={
                                                                workOrder.status?.toLowerCase() === 'completed' ? 'bg-green-900/50 text-green-200' :
                                                                    workOrder.status?.toLowerCase() === 'in_progress' ? 'bg-yellow-900/50 text-yellow-200' :
                                                                        workOrder.status?.toLowerCase() === 'cancelled' ? 'bg-red-900/50 text-red-200' :
                                                                            'bg-blue-900/50 text-blue-200'
                                                            }>
                                                                {workOrder.status ? workOrder.status.charAt(0).toUpperCase() + workOrder.status.slice(1) : 'N/A'}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Pagination */}
                        {totalItems > 0 && (
                            <div className="px-4 sm:px-6 py-4 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
                                <div className="flex items-center gap-4 flex-wrap">
                                    <p className="text-sm text-gray-500">
                                        Showing {rangeStart}–{rangeEnd} of {totalItems} work orders
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm text-gray-400">Show:</label>
                                        <select
                                            value={pageSize}
                                            onChange={(e) => setPageSize(Number(e.target.value))}
                                            className="px-3 py-1.5 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            {pageSizeOptions?.map((size) => (
                                                <option key={size} value={size}>
                                                    {size}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page <= 1}
                                        className="px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                        Previous
                                    </button>
                                    <span className="text-sm text-gray-500 px-2">
                                        Page {page} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page >= totalPages}
                                        className="px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};